import { useEffect } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import type { LoadResult } from './LoadPos'

// Keeps every BOSS MultiPO window of this browser in step (docs/BossMultiPO.md §5): the main page
// and any pop-outs share one BroadcastChannel. A window that saves a cell tells the others, which
// patch their cached grid; a header change makes them reload what they show.

const CHANNEL = 'puresoft-po'

export type SyncMessage =
    | { type: 'item-saved'; store: string; poGuid: string; key: string; qty: number }
    | { type: 'po-changed'; poGuid: string | null }

const channel = (): BroadcastChannel | null =>
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL)

export function broadcast(message: SyncMessage) {
    const ch = channel()
    if (!ch) return
    ch.postMessage(message)
    ch.close()
}

/** A cached load with one cell's quantity replaced, or the same object when the load holds no such PO. */
export function withQty(load: LoadResult | undefined, poGuid: string, key: string, qty: number): LoadResult | undefined {
    if (!load || !load.pos.some(p => p.poGuid.toLowerCase() === poGuid.toLowerCase())) return load
    const quantities = { ...(load.quantities[poGuid] ?? {}) }
    if (qty) quantities[key] = qty
    else delete quantities[key]
    return { ...load, quantities: { ...load.quantities, [poGuid]: quantities } }
}

export function applySyncMessage(queryClient: QueryClient, message: SyncMessage) {
    if (message.type === 'item-saved') {
        // Only loads for the same store show that store's quantities.
        queryClient.setQueriesData<LoadResult>(
            { queryKey: ['multipo-load', message.store] },
            old => withQty(old, message.poGuid, message.key, message.qty))
        queryClient.invalidateQueries({ queryKey: ['multipo-list'] })
    } else {
        queryClient.invalidateQueries({ queryKey: ['multipo-list'] })
        queryClient.invalidateQueries({ queryKey: ['multipo-load'] })
        queryClient.invalidateQueries({ queryKey: ['multipo-header'] })
    }
}

/** Listens for the other windows' changes for as long as the page is open. */
export function useMultiPoSync(queryClient: QueryClient) {
    useEffect(() => {
        const ch = channel()
        if (!ch) return
        ch.onmessage = e => applySyncMessage(queryClient, e.data as SyncMessage)
        return () => ch.close()
    }, [queryClient])
}

/**
 * The pop-out window's name for a PO set, so popping the same set out again focuses the window
 * already showing it instead of opening another.
 */
export function popoutName(store: string, poGuids: string[]): string {
    const text = `${store}|${[...poGuids].map(g => g.toLowerCase()).sort().join(',')}`
    let hash = 0
    for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0
    return `multipo-${(hash >>> 0).toString(36)}`
}
